import { useCallback, useEffect, useRef, useState } from 'react'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'

import NumericKeypad from '@/components/NumericKeypad/NumericKeypad'
import { reset, setCurrentModal, setPaymentStatus } from '../../store/checkoutSlice'
import {
  CheckmarkCircle,
  CheckmarkSvg,
  ConfirmationActions,
  ConfirmationText,
  ModalBackdrop,
  ModalContent,
  ModalWrapper,
  NextButton,
  PrintButton,
  ReceiptOptions,
  SmsLabel,
  SmsSection,
  SmsSentText,
  PhoneDisplay,
  PhoneInputRow,
  PhoneCancelBtn,
  KeypadWrapper,
  NextProgress,
  ReceiptIcon,
} from './ConfirmationModal.styles'

const COUNTDOWN_SECONDS = 9

const ConfirmationModal = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [printing, setPrinting] = useState(false)
  const [smsSent, setSmsSent] = useState(false)
  const [phoneNumber, setPhoneNumber] = useState('')
  const [showSmsInput, setShowSmsInput] = useState(false)
  const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS)
  const countdownRef = useRef(null)

  const handleNextCustomer = useCallback(() => {
    dispatch(reset())
    dispatch(setPaymentStatus('DEFAULT'))
    dispatch(setCurrentModal(null))
  }, [dispatch])

  const startCountdown = useCallback(() => {
    countdownRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countdownRef.current)
          handleNextCustomer()
          return 0
        }
        return prev - 1
      })
    }, 1000)
  }, [handleNextCustomer])

  useEffect(() => {
    startCountdown()
    return () => clearInterval(countdownRef.current)
  }, [startCountdown])

  const resetCountdown = () => {
    setCountdown(COUNTDOWN_SECONDS)
    clearInterval(countdownRef.current)
  }

  const handlePrint = () => {
    if (printing) return
    setPrinting(true)
    resetCountdown()
    setTimeout(() => {
      setPrinting(false)
      startCountdown()
    }, 2000)
  }

  const handleSendSms = () => {
    if (!phoneNumber.trim()) return
    resetCountdown()
    setSmsSent(true)
    setTimeout(() => startCountdown(), 1500)
  }

  const handleCancelSms = () => {
    setShowSmsInput(false)
    setPhoneNumber('')
    startCountdown()
  }

  return (
    <ModalBackdrop>
      <ModalWrapper>
        <ModalContent $compact={showSmsInput && !smsSent}>
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 400, damping: 24 }}
          >
            <CheckmarkCircle $compact={showSmsInput && !smsSent}>
              <CheckmarkSvg viewBox="0 0 52 52">
                <motion.path
                  d="M14 27 l7 7 17-17"
                  fill="none"
                  stroke="#ffffff"
                  strokeWidth="3.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ duration: 0.2, delay: 0.1, ease: 'easeOut' }}
                />
              </CheckmarkSvg>
            </CheckmarkCircle>
          </motion.div>

          <ConfirmationText>
            <h2>{t('confirmationModal.title')}</h2>
            <p>{t('confirmationModal.subtitle')}</p>
          </ConfirmationText>

          <ReceiptOptions>
            <SmsLabel>{t('confirmationModal.receiptDelivery')}</SmsLabel>
            {showSmsInput ? (
              <SmsSection as="div">
                {smsSent ? (
                  <SmsSentText>
                    <ReceiptIcon>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M5 12.5l4.5 4.5L19 7.5" />
                      </svg>
                    </ReceiptIcon>
                    {t('confirmationModal.receiptSent', { phone: phoneNumber })}
                  </SmsSentText>
                ) : (
                  <>
                    <PhoneInputRow>
                      <PhoneDisplay $empty={!phoneNumber}>
                        {phoneNumber || t('confirmationModal.enterPhone')}
                      </PhoneDisplay>
                      <PhoneCancelBtn onClick={handleCancelSms}>{t('confirmationModal.cancel')}</PhoneCancelBtn>
                    </PhoneInputRow>
                    <KeypadWrapper>
                      <NumericKeypad
                        value={phoneNumber}
                        onChange={setPhoneNumber}
                        onSubmit={handleSendSms}
                        maxLength={15}
                      />
                    </KeypadWrapper>
                  </>
                )}
              </SmsSection>
            ) : (
              <SmsSection as="div" $row>
                <PrintButton onClick={handlePrint} disabled={printing}>
                  <ReceiptIcon>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M7 8V3.5h10V8M7 17H4.5a1.5 1.5 0 0 1-1.5-1.5v-6A1.5 1.5 0 0 1 4.5 8h15a1.5 1.5 0 0 1 1.5 1.5v6a1.5 1.5 0 0 1-1.5 1.5H17" />
                      <path d="M7 13.5h10v7H7z" />
                    </svg>
                  </ReceiptIcon>
                  <span>{printing ? t('confirmationModal.printing') : t('confirmationModal.printReceipt')}</span>
                </PrintButton>
                <PrintButton onClick={() => { setShowSmsInput(true); resetCountdown() }}>
                  <ReceiptIcon>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <rect x="6.5" y="2.5" width="11" height="19" rx="2.5" />
                      <path d="M10.5 18.5h3" />
                    </svg>
                  </ReceiptIcon>
                  <span>{t('confirmationModal.textReceipt')}</span>
                </PrintButton>
              </SmsSection>
            )}
          </ReceiptOptions>

          <ConfirmationActions>
            <NextButton onClick={handleNextCustomer} $secondary={showSmsInput && !smsSent}>
              {!(showSmsInput && !smsSent) && <NextProgress style={{ transform: `scaleX(${countdown / COUNTDOWN_SECONDS})` }} />}
              <span>{t('confirmationModal.nextCustomer', { count: countdown })}</span>
            </NextButton>
          </ConfirmationActions>
        </ModalContent>
      </ModalWrapper>
    </ModalBackdrop>
  )
}

export default ConfirmationModal
